import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiAlertTriangle, FiShield, FiLock, FiRefreshCw, FiInfo, FiCheck } from 'react-icons/fi';

const STORAGE_KEY = 'lz_notifications';

export function addNotification(type, title, message) {
  const notifications = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  notifications.push({
    id: Date.now(),
    type,
    title,
    message,
    timestamp: new Date().toISOString(),
    read: false
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  window.dispatchEvent(new CustomEvent('lz-notification-added'));
}

export function getNotifications() {
  const notifications = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  return notifications.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

export function markAllRead() {
  const notifications = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  notifications.forEach(n => { n.read = true; });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
}

export function getUnreadCount() {
  const notifications = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  return notifications.filter(n => !n.read).length;
}

export function clearNotifications() {
  localStorage.removeItem(STORAGE_KEY);
}

const NOTIFICATION_TYPES = {
  sync: { icon: FiRefreshCw, color: '#2563eb', bg: '#dbeafe' },
  drift: { icon: FiAlertTriangle, color: '#d97706', bg: '#fef3c7' },
  compliance: { icon: FiShield, color: '#7c3aed', bg: '#ede9fe' },
  governance: { icon: FiLock, color: '#dc2626', bg: '#fee2e2' },
  security: { icon: FiAlertTriangle, color: '#ea580c', bg: '#fff7ed' },
  system: { icon: FiInfo, color: '#6b7280', bg: '#f3f4f6' },
  success: { icon: FiCheck, color: '#059669', bg: '#d1fae5' }
};

function getRelativeTime(timestamp) {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  return `${diffDays} days ago`;
}

export default function NotificationsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  const loadNotifications = () => {
    const existing = getNotifications();
    if (existing.length === 0) {
      addNotification('system', 'Welcome to Landing Zone Intelligence', 'Get started by onboarding your first cloud tenant');
      setNotifications(getNotifications());
    } else {
      setNotifications(existing);
    }
  };

  useEffect(() => {
    loadNotifications();
    const handler = () => setNotifications(getNotifications());
    window.addEventListener('lz-notification-added', handler);
    return () => window.removeEventListener('lz-notification-added', handler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAllRead = () => {
    markAllRead();
    setNotifications(getNotifications());
  };

  const handleClearAll = () => {
    clearNotifications();
    setNotifications([]);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280'
        }}
        onMouseEnter={e => { e.currentTarget.style.color = '#111827'; }}
        onMouseLeave={e => { e.currentTarget.style.color = '#6b7280'; }}
      >
        <FiBell size={18} />
      </button>

      {unreadCount > 0 && (
        <span style={{
          position: 'absolute',
          top: -4,
          right: -4,
          minWidth: 16,
          height: 16,
          borderRadius: '50%',
          background: '#dc2626',
          color: 'white',
          fontSize: '0.65rem',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          lineHeight: 1,
          padding: '0 4px'
        }}>
          {unreadCount}
        </span>
      )}

      {isOpen && (
        <div style={{
          position: 'absolute',
          right: 0,
          top: 'calc(100% + 8px)',
          width: 360,
          maxHeight: 440,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          border: '1px solid #e5e7eb',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem 1rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#4f46e5',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem 1rem',
                color: '#6b7280'
              }}>
                <FiBell size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                <span style={{ fontSize: '0.85rem' }}>No notifications yet</span>
              </div>
            ) : (
              notifications.map(notification => {
                const typeConfig = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.system;
                const IconComponent = typeConfig.icon;
                return (
                  <div
                    key={notification.id}
                    style={{
                      padding: '0.75rem 1rem',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: '0.75rem',
                      alignItems: 'flex-start',
                      borderLeft: !notification.read ? `3px solid ${typeConfig.color}` : '3px solid transparent',
                      background: !notification.read ? '#fafbff' : 'transparent'
                    }}
                  >
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: typeConfig.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <IconComponent size={14} style={{ color: typeConfig.color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827', marginBottom: 2 }}>
                        {notification.title}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>
                        {notification.message}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                        {getRelativeTime(notification.timestamp)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.6rem 1rem'
          }}>
            <button
              onClick={() => { navigate('/settings'); setIsOpen(false); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#4f46e5',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0
              }}
            >
              Notification Settings
            </button>
            <button
              onClick={handleClearAll}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0
              }}
            >
              Clear All
            </button>
          </div>
        </div>
      )}
    </div>
  );
}